/**
 * `AnalyticsViews` — the shared `ViewsDropdown`, pre-wired for the Analytics page.
 *
 * There is no second views system here, and that is the whole design. `ViewsDropdown`
 * was already generic over its payload — it takes `{viewKey, currentState, applyState,
 * onReset}` and only ever `stableStringify`s the state to compare it, never inspecting
 * the shape — and persistence (`useTableViews` -> user preferences -> jsonb) is equally
 * shape-agnostic. So the page reuses the dropdown, the storage, the active-view
 * highlight, the save/delete flow and `Reset to default` as they stand.
 *
 * Two things were needed and only two:
 *  1. `TableViewState` grew an OPTIONAL `analytics` slot (`types/Preferences.ts`);
 *  2. `ViewsDropdown` grew an OPTIONAL `seededViews` prop, so the four ex-role tabs can
 *     be listed as read-only starting points above the user's own saved views.
 *
 * Both are additive and default to inert, so every existing table view is unaffected.
 *
 * `Reset to default` matters more here than on a table: two people at one brand can now
 * see different analytics, so there has to be one keystroke back to the shared default.
 * It maps to `resetToDefault`, which restores the `Overview` seed wholesale.
 */
import React from 'react'

import { ViewsDropdown } from 'components/display/Table/components/TableViews'
import type { TableViewState } from 'types'

import { ANALYTICS_SEEDED_VIEWS, DEFAULT_ANALYTICS_VIEW_ID } from './analyticsViewSeeds'
import { ANALYTICS_VIEW_KEY } from './useAnalyticsView'

interface AnalyticsViewsProps {
  currentState: TableViewState
  applyState: (state: TableViewState | undefined) => void
  onReset: () => void
}

export const AnalyticsViews = ({ currentState, applyState, onReset }: AnalyticsViewsProps) => (
  <ViewsDropdown
    viewKey={ANALYTICS_VIEW_KEY}
    seededViews={ANALYTICS_SEEDED_VIEWS}
    /**
     * Overview is what the page SHOWS when nothing has been chosen — `useAnalyticsView`
     * applies `DEFAULT_ANALYTICS_VIEW_STATE` on load — so it is what this control must
     * SAY. Without it the page rendered the Overview seed while the trigger read the
     * generic "Views" and no row was checked.
     *
     * Imported from the seeds module rather than taken off `useAnalyticsView`, matching
     * how this file already sources `ANALYTICS_SEEDED_VIEWS` — the hook re-exports both
     * (`seededViews`, `defaultViewId`) and this component has never read either, so
     * going direct keeps one source rather than adding a second path through the hook.
     */
    defaultViewId={DEFAULT_ANALYTICS_VIEW_ID}
    currentState={currentState}
    applyState={applyState}
    onReset={onReset}
  />
)
